import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import bezier from 'bezier-easing';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Fragment, useCallback, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { InvisibleVideo } from '@sorare/core/src/atoms/animations/StackedAlphaVideo/InvisibleVideo';
import { TransparentVideoCanvas } from '@sorare/core/src/atoms/animations/StackedAlphaVideo/TransparentVideoCanvas';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { AddCards } from '@sorare/core/src/atoms/icons/AddCards';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { RotateLeft } from '@sorare/core/src/atoms/icons/RotateLeft';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelS } from '@sorare/core/src/atoms/typography';
import { CommonShards } from '@sorare/core/src/components/CommonShards';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { Card } from '@sorare/core/src/components/card/Card';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';
import { type ColorFormat } from '@sorare/core/src/lib/colors';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { glossary } from '@sorare/core/src/lib/glossary';
import { hapticFeedback } from '@sorare/core/src/lib/haptic';
import { COMMON_GAME_SET } from '@sorare/core/src/set';

import { PackRecap } from '#src/components/commonCard/RevealPackCardsDialog/PackRecap/index.tsx';
import {
  BaseLayout,
  BottomArea,
  CenterArea,
  TopArea,
} from '#src/components/commonCard/RevealPackCardsDialog/ui.ts';

import { CardInfos } from './CardInfos/index.tsx';
import { FirstPackTitle } from './FirstPackTitle/index.tsx';
import { NewCardTag } from './NewCardTag/index.tsx';
import type { TinderCardSwipe_anyCardInterface } from './__generated__/index.graphql.ts';
import { getVideoSources } from './lib.ts';

const CardImgWrapper = styled(animated.div)`
  position: absolute;
  top: 0;
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  user-select: none;
  touch-action: none;
  cursor: grab;
  & > img {
    user-select: none;
    touch-action: none;
  }
  &:focus,
  &:active {
    cursor: grabbing;
  }
`;
const CTAsWrapper = styled(SBHorizontal).attrs({ gap: 3 })`
  align-items: baseline;
  padding-bottom: var(--quadruple-unit);

  ${Vertical} {
    position: relative;
    ${LabelS} {
      width: 100px;
      position: absolute;
      top: calc(100% + var(--unit));
    }
  }
`;
const CoinsBalanceWrapper = styled.div`
  grid-area: topLeft;
`;

const NewTagWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  left: 0;
  transform: translateY(-100%);
`;

const CloseButtonWrapper = styled.div`
  grid-area: topRight;
  justify-self: flex-end;
`;

const TitleWrapper = styled.div`
  grid-area: topCenter;
  align-self: center;
  text-align: center;
`;

const CardCounter = styled(Vertical).attrs({ center: true })`
  grid-area: topCenter;
  align-self: center;
  text-align: center;
`;

const SetSeriesIcon = styled.img.attrs({
  src: COMMON_GAME_SET.visual.cardReveal.swipeIconImage,
})`
  width: 48px;
  height: 48px;
`;

const CardsWrapper = styled(Horizontal).attrs({ center: true })`
  width: var(--card-width);
  aspect-ratio: var(--card-aspect-ratio);
  position: relative;
  isolation: isolate;
`;

const ScrapLabel = styled(LabelS)`
  white-space: nowrap;
`;

const VideoWrapper = styled.div`
  position: absolute;
  left: -80%;
  width: 160%;
  aspect-ratio: var(--card-aspect-ratio);
  pointer-events: none;
`;

const messages = defineMessages({
  keepCard: {
    id: 'TinderCardSwipe.keepCard',
    defaultMessage: 'Keep card',
  },
  scrapCard: {
    id: 'TinderCardSwipe.scrapCard',
    defaultMessage: 'Scrap card',
  },
});

type Props = {
  cards: TinderCardSwipe_anyCardInterface[];
  cardSetSlug: string;
  onContinue: () => void;
  isFirstPack?: boolean;
  packId: string;
  droppedCards: DropCardInput[];
  setDroppedCards: Dispatch<SetStateAction<DropCardInput[]>>;
  packAnimationDone: boolean;
  closeButton: ReactNode;
  showPlayerDetails?: boolean;
  displayRecap: boolean;
  setDisplayRecap: Dispatch<SetStateAction<boolean>>;
  onNextCard: (index: number) => void;
  overlayColor?: ColorFormat;
};

const initialSpringStyles = (index: number) => ({
  x: 0,
  scale: 1,
  rotateZ: 0,
  zIndex: -1 * index,
});

const ACTION_COMPLETED_SWIPE_THRESHOLD = 40;

export const TinderCardSwipe = ({
  cards,
  cardSetSlug,
  onContinue,
  isFirstPack,
  packId,
  droppedCards,
  setDroppedCards,
  packAnimationDone,
  closeButton,
  showPlayerDetails,
  displayRecap,
  setDisplayRecap,
  onNextCard,
  overlayColor,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const isTabletAndAbove = useIsTabletAndAbove();
  const [commonCardShardsCount, setCommonCardShardsCount] = useState(
    getCommonCardShardsCount(currentUser, cardSetSlug) || 0
  );

  const [springStyles, api] = useSprings(cards.length, springIndex => ({
    from: initialSpringStyles(springIndex),
  }));
  const newCardAnimationPlaying = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [videos, setVideos] = useState<HTMLVideoElement[]>([]);

  const [currentMovedIndex, setCurrentMovedIndex] =
    useState<Nullable<number>>(null);
  const isLaptopAndAbove = useIsLaptop();
  const centerAreaRef = useRef<HTMLDivElement>(null);
  const refCallback = useCallback((node: HTMLVideoElement | null) => {
    if (node) {
      const index = parseInt(node.dataset.invisibleVideo || '0', 10);
      setVideos(prev => {
        const newArray = [...prev];
        newArray[index] = node;
        return newArray;
      });
    }
  }, []);

  const currentCardPrice =
    cards.at(currentIndex)?.signedDropCardPrice?.price || 0;

  const containerWidth = isLaptopAndAbove ? 440 : window.innerWidth;
  const swipedCardFinalOffset = containerWidth + 200;

  const currentCard = cards.at(currentIndex);

  const moveToNextIndex = () => {
    newCardAnimationPlaying.current = false;
    setCurrentIndex(prev => prev + 1);
    onNextCard(currentIndex + 1);
    if (currentIndex === cards.length - 1) {
      setDisplayRecap(true);
    }
  };

  const dropCurrentCard = () => {
    if (
      currentCard &&
      !droppedCards.some(c => c.cardSlug === currentCard.slug)
    ) {
      const { slug, signedDropCardPrice } = currentCard;

      if (signedDropCardPrice) {
        setCommonCardShardsCount(prev => prev + signedDropCardPrice.price);
        setDroppedCards(prev => [
          ...prev,
          { cardSlug: slug, signedPrice: signedDropCardPrice.signedPrice },
        ]);
      }
    }
  };

  const moveWithoutDragSpringStyles = ({
    direction,
  }: {
    direction: number;
  }) => ({
    x: direction * swipedCardFinalOffset,
    scale: 1.05,
    rotateZ: (direction * swipedCardFinalOffset) / 20,
    config: {
      tension: 100,
      friction: 50,
    },
  });

  const moveCardWithoutDrag = ({ isDrop }: { isDrop: boolean }) => {
    if (isDrop) {
      dropCurrentCard();
    }
    moveToNextIndex();

    const direction = isDrop ? -1 : 1;
    api.start(i => {
      if (currentIndex !== i) return {};
      return moveWithoutDragSpringStyles({ direction });
    });
  };

  const moveRemainingCards = () => {
    const remainingCardsCount = cards.length - currentIndex;
    setTimeout(
      () => {
        onNextCard(cards.length - 1);
        newCardAnimationPlaying.current = false;
        setCurrentIndex(cards.length - 1);
        requestAnimationFrame(() => {
          setDisplayRecap(true);
        });
      },
      200 + remainingCardsCount * 50
    );
    api.start(i => {
      if (i < currentIndex) return {};
      return {
        ...moveWithoutDragSpringStyles({ direction: 1 }),
        config: {
          ...moveWithoutDragSpringStyles({ direction: 1 }),
          friction: 50 + (i - currentIndex) * 150,
        },
        delay: (i - currentIndex) * 50,
      };
    });
  };

  const undo = () => {
    const previousCard = cards.at(currentIndex - 1);
    const isPreviousCardDropped = droppedCards.some(
      c => c.cardSlug === previousCard?.slug
    );

    if (isPreviousCardDropped) {
      setDroppedCards(prev => prev.slice(0, -1));
      setCommonCardShardsCount(
        prev => prev - (previousCard?.signedDropCardPrice?.price || 0)
      );
    }

    api.start(i => {
      if (currentIndex - 1 !== i) return {};
      return {
        ...initialSpringStyles(i),
        config: {
          tension: 600,
          clamp: true,
        },
      };
    });

    if (currentIndex > 0) {
      newCardAnimationPlaying.current = false;
      setCurrentIndex(prev => prev - 1);
    }
  };

  const bind = useDrag(
    ({
      args: [{ index }],
      movement: [mx],
      last,
      tap,
      initial: [, yInitial],
    }) => {
      const isGone = Math.abs(mx) > ACTION_COMPLETED_SWIPE_THRESHOLD || tap;
      const direction = mx > 0 || tap ? 1 : -1;

      // ignore consequent draggings of a card that is being swiped out
      if (currentIndex !== index) {
        return;
      }

      if (currentMovedIndex !== index) {
        setCurrentMovedIndex(index);
      }

      const { top, height } =
        centerAreaRef.current?.getBoundingClientRect() || { top: 0, height: 0 };
      const rotateFactor =
        ((-1 / 15) * (yInitial - top - height / 2)) / Math.max(height / 2, 1);

      api.start(i => {
        if (index !== i) return {};
        if (last || tap) {
          if (isGone) {
            moveToNextIndex();
            return moveWithoutDragSpringStyles({
              direction,
            });
          }
          setCurrentMovedIndex(null);
          return {
            ...initialSpringStyles(index),
            config: {
              tension: 150,
            },
          };
        }

        return {
          x: mx,
          scale: 1.05,
          rotateZ: mx * rotateFactor,
          config: {
            tension: 800,
            friction: 50,
          },
        };
      });
    }
  );

  if (displayRecap) {
    return (
      <PackRecap
        cards={cards}
        onContinue={onContinue}
        droppedCards={droppedCards}
        packId={packId}
        onBack={() => {
          setDisplayRecap(false);
          undo();
        }}
        isFirstPack={isFirstPack}
        cardSetSlug={cardSetSlug}
      />
    );
  }

  const bumpNewCard = () => {
    api.start(i => {
      if (currentIndex !== i) return {};
      return {
        to: [
          {
            scale: 1.05,
            config: {
              duration: 133,
              easing: bezier(0.05, 0.88, 0.18, 0.99),
            },
          },
          {
            scale: 1,
            config: {
              duration: 233,
              easing: bezier(0.54, 0, 0.33, 1),
            },
          },
        ],
      };
    });
  };

  const showNewCardAnimation =
    packAnimationDone &&
    !!currentCard &&
    !currentCard.previouslyOwnedCard &&
    !newCardAnimationPlaying.current;

  if (showNewCardAnimation) {
    newCardAnimationPlaying.current = true;
    bumpNewCard();
  }

  const currentIndexVideo = videos.at(currentIndex);
  const isCurrentCardIceBreaker =
    cards.at(currentIndex)?.cardEditionName === 'ice_breaker';
  const isCurrentCardSetEdition =
    cards.at(currentIndex)?.cardEditionName ===
    COMMON_GAME_SET.cardSet.activeSlug;
  const appliedOverlayColor = isCurrentCardSetEdition
    ? COMMON_GAME_SET.colors.mainHex
    : overlayColor;

  return (
    <BaseLayout>
      <TopArea>
        {isFirstPack ? (
          <TitleWrapper>
            <FirstPackTitle
              currentIndex={currentIndex}
              numberOfCards={cards.length}
            />
          </TitleWrapper>
        ) : (
          <>
            <CoinsBalanceWrapper>
              <EssenceBalance
                value={commonCardShardsCount}
                rarity={Rarity.common}
                cardSetSlug={cardSetSlug}
              />
            </CoinsBalanceWrapper>
            {isSpecialCardSet(cardSetSlug) && (
              <CardCounter gap={2}>
                <SetSeriesIcon />
                <BrandM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="TinderCardSwipe.cardCounter"
                    defaultMessage="Your cards: {animatedNumber} / {total}"
                    values={{
                      animatedNumber: (
                        <BrandM as="span" bold color="var(--c-white)">
                          <AnimatedNumber value={currentIndex + 1} />
                        </BrandM>
                      ),
                      total: cards.length,
                    }}
                  />
                </BrandM>
              </CardCounter>
            )}
          </>
        )}
        <CloseButtonWrapper>{closeButton}</CloseButtonWrapper>
      </TopArea>
      <CenterArea ref={centerAreaRef}>
        <CardsWrapper>
          <VideoWrapper style={{ zIndex: -1 * cards.length - 1 }}>
            {!!currentIndexVideo && (
              <TransparentVideoCanvas
                overlayColor={
                  isCurrentCardIceBreaker ? undefined : appliedOverlayColor
                }
                video={currentIndexVideo}
                play={packAnimationDone}
              />
            )}
          </VideoWrapper>
          {cards.map((card, index) => {
            const showAnimation =
              index === currentIndex &&
              index !== currentMovedIndex &&
              packAnimationDone;

            const isIceBreaker = card.cardEditionName === 'ice_breaker';

            const videoSources = getVideoSources({
              isNew: !card.previouslyOwnedCard,
              dropRank: card.dropRank,
              isIceBreaker,
            });

            return (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {!!videoSources && (
                  <InvisibleVideo
                    av1Src={videoSources.av1}
                    hevcSrc={videoSources.hevc}
                    ref={refCallback}
                    customDataAttribute={`${index}`}
                  />
                )}
                <CardImgWrapper
                  {...bind({ index })}
                  style={springStyles[index]}
                >
                  {!card.previouslyOwnedCard && (
                    <NewTagWrapper>
                      <NewCardTag show={showAnimation} />
                    </NewTagWrapper>
                  )}
                  <Card width={320} card={card} />
                </CardImgWrapper>
              </Fragment>
            );
          })}
          {!!currentCard && (
            <CardInfos
              card={currentCard}
              showPlayerDetails={showPlayerDetails}
            />
          )}
        </CardsWrapper>
      </CenterArea>
      <BottomArea>
        <Vertical gap={2} center>
          {isFirstPack ? (
            <Horizontal>
              <Button
                size="medium"
                fullWidth={!isTabletAndAbove}
                color="quaternary"
                onClick={moveRemainingCards}
              >
                <FormattedMessage {...glossary.skip} />
              </Button>
              <Button
                size="medium"
                fullWidth={!isTabletAndAbove}
                color="secondary"
                onClick={() => {
                  hapticFeedback();
                  moveCardWithoutDrag({ isDrop: false });
                }}
                disableDebounce
              >
                <FormattedMessage {...glossary.next} />
              </Button>
            </Horizontal>
          ) : (
            <CTAsWrapper>
              {!!currentCard && (
                <Vertical center>
                  <IconButton
                    size="large"
                    disableDebounce
                    color="red"
                    onClick={() => {
                      hapticFeedback();
                      moveCardWithoutDrag({ isDrop: true });
                    }}
                  >
                    <DropCards title={formatMessage(messages.scrapCard)} />
                  </IconButton>
                  <ScrapLabel color="var(--c-nd-700)" className="text-center">
                    <FormattedMessage
                      id="TinderCardSwipe.scrapFor"
                      defaultMessage="Scrap for {amount} {essenceIcon}"
                      values={{
                        amount: currentCardPrice,
                        essenceIcon: (
                          <CommonShards cardSetSlug={cardSetSlug} size={2} />
                        ),
                      }}
                    />
                  </ScrapLabel>
                </Vertical>
              )}
              <IconButton
                size="large"
                disableDebounce
                color="yellow"
                onClick={undo}
                disabled={currentIndex === 0}
              >
                <RotateLeft title={formatMessage(glossary.undo)} />
              </IconButton>
              {!!currentCard && (
                <IconButton
                  size="large"
                  disableDebounce
                  color="green"
                  onClick={() => {
                    hapticFeedback();
                    moveCardWithoutDrag({ isDrop: false });
                  }}
                >
                  <AddCards title={formatMessage(messages.keepCard)} />
                </IconButton>
              )}
            </CTAsWrapper>
          )}
        </Vertical>
      </BottomArea>
    </BaseLayout>
  );
};

TinderCardSwipe.fragments = {
  anyCardInterface: gql`
    fragment TinderCardSwipe_anyCardInterface on AnyCardInterface {
      slug
      name
      previouslyOwnedCard
      dropRank
      cardEditionName
      signedDropCardPrice {
        signedPrice
        price
      }
      ...CardInfos_anyCardInterface
      ...PackRecap_anyCardInterface
      ...Card_anyCard
    }
    ${CardInfos.fragments.anyCardInterface}
    ${Card.fragments.anyCard}
    ${PackRecap.fragments.anyCardInterface}
  ` as TypedDocumentNode<TinderCardSwipe_anyCardInterface>,
};
